import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import useAsyncRequest from "hooks/useAsyncRequest";
import { getErrorMessage } from "utils/errorHelpers";
import { formatMoney } from "utils/formatters/CurrencyFormatters";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { SalesMotion, TaxEstimatePreviewOutcome } from "client/api-v2";
import type { OrderDto, OrderTaxEstimateDto, PriceBreakdown } from "client/api-v2";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { EmptyValue } from "components/MutedPlaceholder";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { BuilderSection } from "areas/orders/builder/BuilderSection";
import { CloneRateCardDialog } from "areas/orders/builder/sections/CloneRateCardDialog";
import type { QuotePricePreview } from "areas/orders/builder/useQuotePricePreview";
import { orderToasts } from "areas/orders/components/chrome/orderToasts";
import { vectorLabels } from "areas/orders/orderConstants";
import {
  assignPlanDefaultRateCard,
  createOrderTaxEstimate,
  getOrderRateCardOperations,
} from "areas/orders/services/orderService";
import type { OrderQuantityFormRow } from "areas/orders/utils/orderQuantities";
import {
  maxPricedQuantity,
  restoredPricedValue,
  validateOverrideReason,
  validatePricedQuantity,
} from "areas/orders/utils/orderQuantities";
import { currentTaxEstimate, staleTaxEstimateReason, taxRateLabel } from "areas/orders/utils/orderTaxEstimate";
import { rateCardRoutes } from "areas/rate-cards/rate-card-routes";
import type { SectionProps } from "./SectionProps";

const taxOutcomeReasons: Record<TaxEstimatePreviewOutcome, string> = {
  [TaxEstimatePreviewOutcome.Saved]: "",
  [TaxEstimatePreviewOutcome.NoAddressToCalculateTaxAgainst]:
    "This customer has no billing address complete enough to calculate tax against. Add a city, postal code and country to their billing account.",
  [TaxEstimatePreviewOutcome.SubscriptionNotYetInZuora]:
    "This subscription is not in the billing system yet, so there is no renewal to price.",
  [TaxEstimatePreviewOutcome.ZuoraPreviewUnavailable]:
    "The billing system did not return a usable price. Try again in a few minutes.",
  [TaxEstimatePreviewOutcome.PreviewedPriceDidNotMatchTheQuote]:
    "The billing system priced this quote differently from its own total, so no estimate was saved. Raise this with engineering.",
};

export const rateCardCloneRoleMissingMessage = `You need the ${RbacPolicies.RateCards.ReadWrite} role to clone a rate card.`;
export const unsavedEditsBeforeRateCardMessage = "Save your changes before changing the rate card.";
export const readingRateCardOperationsMessage = "Reading which rate card actions this quote allows.";
export const rateCardOperationsUnreadableMessage =
  "The rate card actions for this quote could not be read, so the card cannot be changed.";
export const defaultRateCardUnnamedMessage =
  "The plan's default rate card was not named, so the quote cannot be moved onto it.";

const usageHistoryReason = "Usage over time is not recorded yet. The measured figures above are the latest reading.";

const predictableGrowthReason =
  "Predictable Growth settings are not held on an order yet, so there is nothing to read here.";

const invalidQuantityNotice = "Enter a valid quantity in each row to update the price.";

const previewFailedNotice = "The price could not be updated. Save the quote to reprice it.";

export const saveBeforeTaxMessage = "Save your changes before calculating tax.";

const taxEstimateCaveat = "Tax is an estimate only. Final tax is determined at invoicing.";

export const zeroTotalWarning =
  "The total is $0. If the customer should pay nothing, check whether a Free plan fits better.";

const discountAndRoundingLabel = "Discount and rounding";

const quantityInputConstraints = { min: 0, max: maxPricedQuantity, step: 1 };

function quantityFieldError(value: string): { error: boolean; helperText: string | undefined } {
  const problem = validatePricedQuantity(value);
  return { error: problem !== true, helperText: problem === true ? undefined : problem };
}

function InfoTooltip({ title, testId }: { title: string; testId: string }) {
  return (
    <Tooltip title={title}>
      <InfoOutlinedIcon
        data-testid={testId}
        fontSize="inherit"
        sx={{ ml: 0.5, color: "text.secondary", cursor: "help", verticalAlign: "middle" }}
      />
    </Tooltip>
  );
}

function MeasuredUsageTable({
  rows,
  readOnly,
  onChange,
  onReset,
}: {
  rows: OrderQuantityFormRow[];
  readOnly: boolean;
  onChange: (index: number, changes: Partial<OrderQuantityFormRow>) => void;
  onReset: (index: number) => void;
}) {
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label="Measured usage">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align="right">As measured</TableCell>
            <TableCell>Override</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const label = vectorLabels[row.vector];
            const reasonProblem = validateOverrideReason(row) !== true;
            return (
              <TableRow key={row.vector}>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                  </Typography>
                  {row.measuredAt !== null && (
                    <Typography variant="caption" color="text.secondary">
                      Measured {formatDateOnly(getOrParseDateTime(row.measuredAt))}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: "top", fontWeight: 600 }}>
                  {row.measuredValue === null ? <EmptyValue /> : row.measuredValue}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField
                    size="small"
                    type="number"
                    value={row.pricedValue}
                    {...quantityFieldError(row.pricedValue)}
                    disabled={readOnly}
                    slotProps={{
                      htmlInput: { ...quantityInputConstraints, "aria-label": `Override ${label}` },
                    }}
                    onChange={(event) => onChange(index, { pricedValue: event.target.value })}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    placeholder="Reason for override"
                    value={row.overrideReason}
                    error={reasonProblem}
                    disabled={readOnly}
                    slotProps={{ htmlInput: { "aria-label": `Reason for override ${label}` } }}
                    onChange={(event) => onChange(index, { overrideReason: event.target.value })}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  {row.measuredValue !== null && (
                    <Tooltip title="Reset to measured">
                      {/* Why: a disabled button fires no events, so the tooltip listens on the wrapper. */}
                      <span>
                        <IconButton
                          size="small"
                          disabled={readOnly}
                          aria-label={`Reset ${label} to measured`}
                          onClick={() => onReset(index)}
                        >
                          <RestartAltIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ForecastUsageTable({
  rows,
  readOnly,
  onChange,
}: {
  rows: OrderQuantityFormRow[];
  readOnly: boolean;
  onChange: (index: number, changes: Partial<OrderQuantityFormRow>) => void;
}) {
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label="Forecast usage">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align="right">Forecast</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const label = vectorLabels[row.vector];
            return (
              <TableRow key={row.vector}>
                <TableCell>{label}</TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={row.pricedValue}
                    {...quantityFieldError(row.pricedValue)}
                    disabled={readOnly}
                    slotProps={{
                      htmlInput: { ...quantityInputConstraints, "aria-label": `Forecast ${label}` },
                    }}
                    onChange={(event) => onChange(index, { pricedValue: event.target.value })}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LineItemsTable({ price }: { price: PriceBreakdown }) {
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label="Line items">
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell align="right">Count</TableCell>
            <TableCell align="right">
              List
              <InfoTooltip title="Flat rate-card price before any volume discount" testId="list-price-info" />
            </TableCell>
            <TableCell align="right">
              Unit
              <InfoTooltip
                title="The rate-card price actually charged per unit at this volume tier."
                testId="unit-price-info"
              />
            </TableCell>
            <TableCell align="right">Cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {price.lines.map((line) => (
            <TableRow key={line.vector}>
              <TableCell>{vectorLabels[line.vector]}</TableCell>
              <TableCell align="right">{line.quantity.toLocaleString("en-US")}</TableCell>
              <TableCell
                align="right"
                sx={{
                  color: "text.secondary",
                  textDecoration: line.discountFraction > 0 ? "line-through" : "none",
                }}
              >
                {formatMoney(line.listUnitPrice, price.currency)}
              </TableCell>
              <TableCell align="right">{formatMoney(line.discountedUnitPrice, price.currency)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatMoney(line.lineTotal, price.currency)}
              </TableCell>
            </TableRow>
          ))}
          {price.fixedFees.map((fee) => (
            <TableRow key={fee.description}>
              <TableCell>{fee.description}</TableCell>
              <TableCell align="right">
                <EmptyValue />
              </TableCell>
              <TableCell align="right">
                <EmptyValue />
              </TableCell>
              <TableCell align="right">
                <EmptyValue />
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatMoney(fee.amount, price.currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function VolumeDiscountSummary({ price }: { price: PriceBreakdown }) {
  const discounted = price.lines.filter((line) => line.discountFraction > 0);

  return (
    <Box>
      <Typography variant="body2">Volume discount applied:</Typography>
      {discounted.length === 0 ? (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          None
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {discounted.map((line) => (
            <Typography component="li" variant="body2" key={line.vector}>
              {vectorLabels[line.vector]}: {formatMoney(line.listUnitPrice - line.discountedUnitPrice, price.currency)}{" "}
              off per unit
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function PredictableGrowthPanel() {
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Predictable Growth
        </Typography>
        <Button size="small" variant="text" disabled aria-label="Edit Predictable Growth">
          Edit
        </Button>
      </Stack>
      {["Budget-protection cap", "Price-increase cap", "Minimum renewal amount"].map((setting) => (
        <Typography variant="body2" key={setting}>
          {setting}: <EmptyValue />
        </Typography>
      ))}
      <Typography variant="caption" color="text.secondary">
        {predictableGrowthReason}
      </Typography>
    </Stack>
  );
}

function TotalsPanel({
  price,
  estimate,
  canCalculate,
  calculating,
  unavailableReason,
  onCalculate,
}: {
  price: PriceBreakdown;
  estimate: OrderTaxEstimateDto | undefined;
  canCalculate: boolean;
  calculating: boolean;
  unavailableReason: string | undefined;
  onCalculate: () => void;
}) {
  return (
    <Stack spacing={1} sx={{ maxWidth: 390, width: "100%" }}>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">
          Subtotal (excl. tax)
        </Typography>
        <Typography variant="body2">{formatMoney(price.subtotal, price.currency)}</Typography>
      </Stack>
      {price.overallDiscount > 0 && (
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            {discountAndRoundingLabel}
          </Typography>
          <Typography variant="body2">−{formatMoney(price.overallDiscount, price.currency)}</Typography>
        </Stack>
      )}
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">
          Net
        </Typography>
        <Typography variant="body2">{formatMoney(price.total, price.currency)}</Typography>
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Tax estimate ({taxRateLabel(estimate)})
          </Typography>
          <Button size="small" variant="text" disabled={!canCalculate} loading={calculating} onClick={onCalculate}>
            Calculate
          </Button>
        </Stack>
        <Typography variant="body2">
          {estimate ? formatMoney(estimate.estimatedTaxAmount, price.currency) : <EmptyValue />}
        </Typography>
      </Stack>
      <Divider />
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Typography variant="subtitle2">{estimate ? "Total (incl. tax estimate)" : "Total"}</Typography>
        <Typography variant="subtitle2">
          {formatMoney(estimate?.estimatedTotalIncludingTax ?? price.total, price.currency)}
        </Typography>
      </Stack>
      {unavailableReason !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {unavailableReason}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
        {taxEstimateCaveat}
      </Typography>
    </Stack>
  );
}

export type PricingSectionProps = SectionProps & {
  isDirty: boolean;
  pricePreview: QuotePricePreview;
  onOrderChanged: (order: OrderDto) => void;
};

export function PricingSection({
  order,
  draft,
  setField,
  readOnly,
  isDirty,
  pricePreview,
  onOrderChanged,
}: PricingSectionProps) {
  const { price } = pricePreview;
  const { sendNewAlert } = useAlerts();
  const { isAuthorized } = useRbacContext();
  const [estimate, setEstimate] = useState<OrderTaxEstimateDto | undefined>(order.taxEstimate ?? undefined);
  const [taxOutcome, setTaxOutcome] = useState<TaxEstimatePreviewOutcome | undefined>(undefined);
  const [calculating, setCalculating] = useState(false);
  const [taxError, setTaxError] = useState<unknown>(undefined);
  const [isCloning, setIsCloning] = useState(false);
  const [isAssignPrompted, setIsAssignPrompted] = useState(false);

  const loadRateCardOperations = React.useCallback(() => getOrderRateCardOperations(order.odNumber), [order.odNumber]);
  const {
    data: rateCardOperations,
    loading: readingRateCardOperations,
    refresh: refreshRateCardOperations,
  } = useAsyncRequest(loadRateCardOperations);

  const canCloneRateCard = isAuthorized(RbacPolicies.RateCards.ReadWrite);
  const canOpenRateCard = isAuthorized(RbacPolicies.RateCards.Read);

  const rows = draft.quantities;
  const hasMeasuredUsage = rows.some((row) => row.measuredValue !== null);
  const overrideReasonMissing = rows.some((row) => validateOverrideReason(row) !== true);

  const usageTitle = hasMeasuredUsage
    ? "Actual usage"
    : order.salesMotion === SalesMotion.New
    ? "First year committed usage"
    : "Forecast usage";

  const changeRow = (index: number, changes: Partial<OrderQuantityFormRow>) =>
    setField(
      "quantities",
      rows.map((row, position) => (position === index ? { ...row, ...changes } : row))
    );

  const resetRow = (index: number) => {
    const row = rows[index];
    if (row !== undefined) {
      changeRow(index, { pricedValue: restoredPricedValue(row), overrideReason: "" });
    }
  };

  const resetAllRows = () =>
    setField(
      "quantities",
      rows.map((row) => ({ ...row, pricedValue: restoredPricedValue(row), overrideReason: "" }))
    );

  const canCalculateTax = !readOnly && !calculating && !isDirty;
  const failedOutcome =
    taxOutcome !== undefined && taxOutcome !== TaxEstimatePreviewOutcome.Saved ? taxOutcome : undefined;
  const taxUnavailableReason = isDirty
    ? saveBeforeTaxMessage
    : failedOutcome
    ? taxOutcomeReasons[failedOutcome]
    : staleTaxEstimateReason(estimate);

  const calculateTax = async () => {
    setCalculating(true);
    setTaxOutcome(undefined);
    setTaxError(undefined);
    try {
      const result = await createOrderTaxEstimate(order.odNumber);
      if (result.outcome === TaxEstimatePreviewOutcome.Saved && result.taxEstimate) {
        setEstimate(result.taxEstimate);
      } else {
        setTaxOutcome(result.outcome);
      }
    } catch (error) {
      setTaxError(error);
    } finally {
      setCalculating(false);
    }
  };

  const rateCardProbeReason = readingRateCardOperations
    ? readingRateCardOperationsMessage
    : rateCardOperations === undefined
    ? rateCardOperationsUnreadableMessage
    : undefined;
  const unsavedEditsReason = isDirty ? unsavedEditsBeforeRateCardMessage : undefined;
  const isReason = (reason: string | null | undefined): reason is string => !!reason;

  const cloneRefusals = [
    unsavedEditsReason,
    rateCardProbeReason,
    canCloneRateCard ? undefined : rateCardCloneRoleMissingMessage,
    rateCardOperations?.canClone === false ? rateCardOperations.cloneUnavailableReason : undefined,
  ].filter(isReason);

  const assignDefaultRefusals = [
    unsavedEditsReason,
    rateCardProbeReason,
    rateCardOperations?.canAssignDefault === false ? rateCardOperations.assignDefaultUnavailableReason : undefined,
    rateCardOperations?.canAssignDefault === true && rateCardOperations.defaultRateCardCode === null
      ? defaultRateCardUnnamedMessage
      : undefined,
  ].filter(isReason);

  const rateCardActionReasons = [...new Set([...cloneRefusals, ...assignDefaultRefusals])];

  const planDefaultRateCardCode =
    rateCardOperations?.canAssignDefault === true ? rateCardOperations.defaultRateCardCode : null;

  const applyRateCardChange = (updated: OrderDto) => {
    onOrderChanged(updated);
    refreshRateCardOperations();
    sendNewAlert(orderToasts.rateCardChanged(updated.rateCardCode), AlertSeverity.Success);
  };

  return (
    <BuilderSection title="Pricing">
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.secondary">
          Using rate card
        </Typography>
        {canOpenRateCard ? (
          <Link
            component={RouterLink}
            to={rateCardRoutes.detail(order.rateCardCode).root}
            sx={{ fontFamily: "monospace" }}
          >
            {order.rateCardCode}
          </Link>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {order.rateCardCode}
          </Typography>
        )}
        {!readOnly && (
          <>
            <Button size="small" variant="text" disabled={cloneRefusals.length > 0} onClick={() => setIsCloning(true)}>
              Clone and update with new rate card
            </Button>
            <Button
              size="small"
              variant="text"
              disabled={assignDefaultRefusals.length > 0}
              onClick={() => setIsAssignPrompted(true)}
            >
              Assign to default rate card
            </Button>
          </>
        )}
      </Stack>
      {!readOnly && (
        <Stack spacing={0.5}>
          {rateCardActionReasons.map((reason) => (
            <Typography key={reason} variant="caption" color="text.secondary">
              {reason}
            </Typography>
          ))}
        </Stack>
      )}
      {!readOnly && (
        <>
          <CloneRateCardDialog
            open={isCloning}
            order={order}
            onCancel={() => setIsCloning(false)}
            onCloned={(cloned) => {
              setIsCloning(false);
              applyRateCardChange(cloned);
            }}
          />
          <ActionDialog
            show={isAssignPrompted}
            title="Assign the plan default rate card"
            description={
              <>
                This quote is priced from <strong>{order.rateCardCode}</strong>. Assigning the plan default moves it
                onto <strong>{planDefaultRateCardCode}</strong>, the default card for the{" "}
                <strong>{order.planName}</strong> plan and the <strong>{order.salesMotion}</strong> sales motion. The
                new card reprices the quote, and the negotiated prices are not kept.
              </>
            }
            confirmText="Assign default"
            onCancel={() => setIsAssignPrompted(false)}
            onConfirm={async () => {
              try {
                const assigned = await assignPlanDefaultRateCard(order.odNumber);
                setIsAssignPrompted(false);
                applyRateCardChange(assigned);
              } catch (error) {
                sendNewAlert(getErrorMessage(error), AlertSeverity.Error);
              }
            }}
          />
        </>
      )}

      <Typography variant="subtitle2">{usageTitle}</Typography>

      {hasMeasuredUsage ? (
        <>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
            <Button size="small" variant="text" disabled>
              View usage history
            </Button>
            <Button size="small" variant="text" disabled={readOnly} onClick={resetAllRows}>
              Reset to measured
            </Button>
          </Stack>
          {overrideReasonMissing && <Alert severity="warning">Please provide a reason for each override.</Alert>}
          <MeasuredUsageTable rows={rows} readOnly={readOnly} onChange={changeRow} onReset={resetRow} />
          <Typography variant="caption" color="text.secondary">
            {usageHistoryReason}
          </Typography>
        </>
      ) : (
        <ForecastUsageTable rows={rows} readOnly={readOnly} onChange={changeRow} />
      )}

      {pricePreview.hasInvalidQuantity && (
        <Typography variant="caption" color="text.secondary">
          {invalidQuantityNotice}
        </Typography>
      )}
      {pricePreview.previewFailed && (
        <Typography variant="caption" color="text.secondary">
          {previewFailedNotice}
        </Typography>
      )}
      <LineItemsTable price={price} />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
      >
        <VolumeDiscountSummary price={price} />
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 390 }}>
          <PredictableGrowthPanel />
          <Divider />
          <TotalsPanel
            price={price}
            estimate={isDirty ? undefined : currentTaxEstimate(estimate)}
            canCalculate={canCalculateTax}
            calculating={calculating}
            unavailableReason={taxUnavailableReason}
            onCalculate={calculateTax}
          />
        </Stack>
      </Stack>

      {price.total === 0 && <Alert severity="warning">{zeroTotalWarning}</Alert>}

      {!!taxError && <Alert severity="error">{getErrorMessage(taxError)}</Alert>}
    </BuilderSection>
  );
}
